/**
 * MainLayout Component
 *
 * Main application layout with sidebar navigation and header.
 */
import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Text,
  Badge,
} from '@fluentui/react-components';
import { SignOutRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  sidebar: {
    width: '240px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingVerticalM,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
  },
  nav: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    textDecoration: 'none',
    color: tokens.colorNeutralForeground1,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  main: {
    flex: 1,
    padding: tokens.spacingVerticalL,
    overflowY: 'auto',
  },
  userMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType;
  activeIcon?: React.ComponentType;
}

export interface MainLayoutProps {
  productName: string;
  LogoIcon: React.ComponentType;
  navItems: NavItem[];
  showAdminSection?: boolean;
  adminItems?: NavItem[];
  user: {
    displayName: string;
    email: string;
  };
  userRole?: string;
  roleBadgeColor?: 'danger' | 'subtle' | 'informative' | 'success' | 'warning' | 'important' | 'severe';
  onSignOut: () => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  productName,
  LogoIcon,
  navItems,
  showAdminSection,
  adminItems,
  user,
  userRole,
  roleBadgeColor = 'subtle',
  onSignOut,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          <LogoIcon />
          <Text weight="semibold" size={400}>
            {productName}
          </Text>
        </div>
        <nav className={styles.nav}>
          {navItems.map((item) => (
            <a key={item.path} href={item.path} className={styles.navItem}>
              <item.icon />
              <span>{item.label}</span>
            </a>
          ))}
          {showAdminSection && adminItems && adminItems.length > 0 && (
            <>
              <Text size={200} weight="semibold" style={{ marginTop: tokens.spacingVerticalL }}>
                Admin
              </Text>
              {adminItems.map((item) => (
                <a key={item.path} href={item.path} className={styles.navItem}>
                  <item.icon />
                  <span>{item.label}</span>
                </a>
              ))}
            </>
          )}
        </nav>
      </aside>

      {/* Main Content */}
      <div className={styles.content}>
        {/* Header */}
        <header className={styles.header}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="subtle" className={styles.userMenu}>
                <Avatar name={user.displayName} size={32} />
                <div>
                  <Text weight="semibold">{user.displayName}</Text>
                  {userRole && (
                    <Badge appearance="filled" color={roleBadgeColor} size="small">
                      {userRole}
                    </Badge>
                  )}
                </div>
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<SignOutRegular />} onClick={onSignOut}>
                  Sign out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </header>

        {/* Page Content */}
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
